/**
 * Scan management API methods (create, list, cancel, schedule).
 */

import apiClient from './client';
import type { Scan, ScanCreateRequest, ScanSchedule, ScanScheduleCreateRequest, ScanScheduleUpdateRequest, ScanEngineOutcome } from '@/types/scan';

/** List scans for the current tenant with optional status filter. */
export async function getScans(status?: string,pagination?:{skip:number;limit:number}): Promise<Scan[]> {
  const params = {...(status?{status}:{}),...pagination};
  const { data } = await apiClient.get<Scan[]>('/v1/scans', { params });
  return data;
}

/** Create and trigger a new scan. */
export async function createScan(payload: ScanCreateRequest): Promise<Scan> {
  const { data } = await apiClient.post<Scan>('/v1/scans', payload);
  return data;
}

/** Get a single scan by ID. */
export async function getScan(scanId: string): Promise<Scan> {
  const { data } = await apiClient.get<Scan>(`/v1/scans/${scanId}`);
  return data;
}

/** Per-engine outcomes for a scan (served directly by the Worker). */
export async function getScanEngines(scanId: string): Promise<{ scan_id: string; engines: ScanEngineOutcome[] }> {
  const { data } = await apiClient.get<{ scan_id: string; engines: ScanEngineOutcome[] }>(`/v1/scans/${scanId}/engines`);
  return data;
}

/** Cancel a pending or running scan. */
export async function cancelScan(scanId: string): Promise<Scan> {
  const { data } = await apiClient.post<Scan>(`/v1/scans/${scanId}/cancel`);
  return data;
}

/** List recurring scan schedules. */
export async function getSchedules(): Promise<ScanSchedule[]> {
  const { data } = await apiClient.get<ScanSchedule[]>('/v1/scans/schedules');
  return data;
}

/** Create a recurring scan schedule. */
export async function createSchedule(payload: ScanScheduleCreateRequest): Promise<ScanSchedule> {
  const { data } = await apiClient.post<ScanSchedule>('/v1/scans/schedules', payload);
  return data;
}

/** Delete a scan by ID. */
export async function deleteScan(scanId: string): Promise<void> {
  await apiClient.delete(`/v1/scans/${scanId}`);
}

/** Update a scan schedule (name, cron, scan type, active state). */
export async function updateSchedule(
  scheduleId: string,
  payload: ScanScheduleUpdateRequest,
): Promise<ScanSchedule> {
  const { data } = await apiClient.patch<ScanSchedule>(`/v1/scans/schedules/${scheduleId}`, payload);
  return data;
}

/** Delete a scan schedule by ID. */
export async function deleteSchedule(scheduleId: string): Promise<void> {
  await apiClient.delete(`/v1/scans/schedules/${scheduleId}`);
}
